import { useEffect, useState } from "react";
import { Download as DownloadIcon, Monitor, Apple, Terminal, Check, Star, FileDown } from "lucide-react";

// Public releases live in a separate repo from the source code (see
// .github/workflows/release.yml for why) -- change this to match.
const RELEASES_REPO = "YOUR_GITHUB_USERNAME/tokenguard-releases";

const PLATFORMS = [
  {
    id: "windows",
    name: "Windows",
    icon: <Monitor size={24} />,
    color: "#0078d4",
    archs: [
      { arch: "x86_64", label: "64-bit installer", ext: ".exe", size: "~8 MB" },
      { arch: "aarch64", label: "ARM64 installer", ext: ".exe", size: "~8 MB" },
    ],
    note: "Windows 10 or later",
  },
  {
    id: "macos",
    name: "macOS",
    icon: <Apple size={24} />,
    color: "#1a1a1a",
    archs: [
      { arch: "x86_64", label: "Intel", ext: ".dmg", size: "~12 MB" },
      { arch: "aarch64", label: "Apple Silicon", ext: ".dmg", size: "~12 MB" },
    ],
    note: "macOS 11 (Big Sur) or later",
  },
  {
    id: "linux",
    name: "Linux",
    icon: <Terminal size={24} />,
    color: "#e95420",
    archs: [
      { arch: "x86_64", label: "AppImage", ext: ".AppImage", size: "~14 MB" },
      { arch: "aarch64", label: "AppImage (ARM64)", ext: ".AppImage", size: "~14 MB" },
    ],
    note: "Most distributions",
  },
];

function detectOS() {
  const ua = navigator.userAgent;
  if (ua.includes("Win")) return "windows";
  if (ua.includes("Mac")) return "macos";
  if (ua.includes("Linux")) return "linux";
  return null;
}

export default function Download() {
  const [detected, setDetected] = useState(null);
  const [downloading, setDownloading] = useState(null);
  // Real per-file download URLs from GitHub's latest release -- asset
  // filenames are versioned (TokenGuard_1.2.3_x86_64.exe), so there's no
  // static URL that's always correct; this is fetched once on mount.
  // null = still loading, {} = fetch failed (falls back to linking the
  // whole GitHub release page instead of a specific file).
  const [assetUrls, setAssetUrls] = useState(null);
  const [releasePageUrl, setReleasePageUrl] = useState(`https://github.com/${RELEASES_REPO}/releases/latest`);

  useEffect(() => {
    setDetected(detectOS());
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`https://api.github.com/repos/${RELEASES_REPO}/releases/latest`)
      .then((res) => {
        if (!res.ok) throw new Error(`GitHub API returned ${res.status}`);
        return res.json();
      })
      .then((release) => {
        if (cancelled) return;
        setReleasePageUrl(release.html_url || releasePageUrl);
        const urls = {};
        for (const asset of release.assets || []) {
          // Match "TokenGuard_<version>_<arch><ext>" and key by arch+ext,
          // same key shape PlatformCard already uses (platform-arch).
          const m = asset.name.match(/^TokenGuard_[\d.]+_(x86_64|aarch64)(\.exe|\.dmg|\.AppImage)$/);
          if (m) urls[`${m[1]}${m[2]}`] = asset.browser_download_url;
        }
        setAssetUrls(urls);
      })
      .catch((err) => {
        // Rate-limited or offline -- fall back gracefully to linking the
        // GitHub release page itself rather than a broken/guessed URL.
        console.warn("Couldn't fetch latest release info:", err);
        if (!cancelled) setAssetUrls({});
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="section" style={{ paddingTop: 80, paddingBottom: 40, background: "var(--cream)", textAlign: "center" }}>
        <div className="container">
          <h1 style={{ fontSize: "clamp(30px, 4vw, 44px)", marginBottom: 14 }}>
            Download TokenGuard
          </h1>
          <p style={{ fontSize: 17, color: "var(--slate)", maxWidth: 560, margin: "0 auto" }}>
            Available for Windows, macOS, and Linux. Choose your platform and architecture below.
          </p>
        </div>
      </section>

      {/* All Platforms Grid */}
      <section style={{ paddingBottom: 80 }}>
        <div className="container">
          <div className="download-grid" style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 24
          }}>
            {PLATFORMS.map((p) => (
              <PlatformCard
                key={p.id}
                platform={p}
                isRecommended={detected === p.id}
                downloading={downloading}
                setDownloading={setDownloading}
                assetUrls={assetUrls}
                releasePageUrl={releasePageUrl}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Installation Steps */}
      <section className="section" style={{ background: "var(--cream)" }}>
        <div className="container">
          <h2 style={{ fontSize: 22, textAlign: "center", marginBottom: 40 }}>
            Installation
          </h2>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 24,
            maxWidth: 800,
            margin: "0 auto"
          }}>
            <Step n={1} title="Download" desc="Pick your platform above and grab the installer." />
            <Step n={2} title="Install" desc="Run the .exe, .dmg, or .AppImage. No admin rights needed." />
            <Step n={3} title="Connect" desc="Sign in or create an account. Point to your backend URL." />
          </div>
        </div>
      </section>

      {/* System Requirements */}
      <section className="section">
        <div className="container" style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ fontSize: 22, textAlign: "center", marginBottom: 24 }}>
            System Requirements
          </h2>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16
          }}>
            <ReqCard title="Windows" items={["Windows 10 or later", "x86_64 or ARM64", "~20 MB disk space"]} />
            <ReqCard title="macOS" items={["macOS 11 (Big Sur) or later", "Intel or Apple Silicon", "~25 MB disk space"]} />
            <ReqCard title="Linux" items={["Most distributions", "x86_64 or ARM64", "~30 MB disk space"]} />
          </div>
        </div>
      </section>
    </>
  );
}

function PlatformCard({ platform, isRecommended, downloading, setDownloading, assetUrls, releasePageUrl }) {
  return (
    <div style={{
      background: "var(--card-surface)",
      border: `2px solid ${isRecommended ? "var(--accent)" : "var(--stone)"}`,
      borderRadius: "var(--radius-lg)",
      padding: 28,
      position: "relative",
      transition: "box-shadow 0.2s ease",
      boxShadow: isRecommended ? "0 8px 32px rgba(46,125,107,0.12)" : "none"
    }}>
      {isRecommended && (
        <div style={{
          position: "absolute",
          top: -1,
          right: 20,
          background: "var(--accent)",
          color: "white",
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          padding: "6px 14px",
          borderRadius: "0 0 var(--radius-sm) var(--radius-sm)",
          display: "flex",
          alignItems: "center",
          gap: 6
        }}>
          <Star size={12} fill="white" /> Recommended
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{
          width: 48, height: 48, borderRadius: "var(--radius-md)",
          background: `${platform.color}15`, color: platform.color,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          {platform.icon}
        </div>
        <div>
          <h3 style={{ fontSize: 20, marginBottom: 2 }}>{platform.name}</h3>
          <p style={{ fontSize: 12.5, color: "var(--muted-slate)" }}>{platform.note}</p>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {platform.archs.map((a) => {
          const key = `${platform.id}-${a.arch}`;
          const isDownloading = downloading === key;
          return (
            <button
              key={a.arch}
              className="btn"
              style={{
                width: "100%",
                justifyContent: "space-between",
                padding: "12px 16px",
                fontSize: 14,
                background: isDownloading ? "var(--cream)" : "var(--warm-white)",
                border: "1.5px solid var(--stone)",
                color: "var(--charcoal)",
                borderRadius: "var(--radius-sm)"
              }}
              onClick={() => handleDownload(a.arch, a.ext, assetUrls, releasePageUrl, key, setDownloading)}
              disabled={isDownloading || assetUrls === null}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {isDownloading ? (
                  <Check size={16} color="var(--accent)" />
                ) : (
                  <FileDown size={16} color="var(--accent)" />
                )}
                <span style={{ fontWeight: 500 }}>
                  {isDownloading ? "Downloaded" : a.label}
                </span>
              </span>
              <span style={{ fontSize: 12, color: "var(--muted-slate)", fontWeight: 400 }}>
                {a.size}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function handleDownload(arch, ext, assetUrls, releasePageUrl, key, setDownloading) {
  setDownloading(key);
  // assetUrls === {} means the GitHub API fetch failed (rate-limited or
  // offline) -- send them to the release page itself rather than a URL we
  // can't actually confirm is correct. assetUrls[archExt] missing means the
  // latest release simply doesn't have that platform/arch built (shouldn't
  // normally happen -- all 6 combos are built every release -- but fail
  // safely rather than link to a 404).
  const url = assetUrls?.[`${arch}${ext}`] || releasePageUrl;
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => setDownloading(null), 2000);
}

function Step({ n, title, desc }) {
  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%",
        background: "var(--accent)", color: "white",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontWeight: 700, fontSize: 14, flexShrink: 0
      }}>
        {n}
      </div>
      <div>
        <h4 style={{ fontSize: 15, marginBottom: 4 }}>{title}</h4>
        <p style={{ fontSize: 13.5, color: "var(--slate)" }}>{desc}</p>
      </div>
    </div>
  );
}

function ReqCard({ title, items }) {
  return (
    <div style={{
      background: "var(--card-surface)",
      border: "1px solid var(--stone)",
      borderRadius: "var(--radius-md)",
      padding: 20
    }}>
      <h4 style={{ fontSize: 14, marginBottom: 10, color: "var(--charcoal)" }}>{title}</h4>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((item, i) => (
          <li key={i} style={{ fontSize: 13, color: "var(--slate)", display: "flex", alignItems: "center", gap: 8 }}>
            <Check size={14} color="var(--accent)" /> {item}
          </li>
        ))}
      </ul>
    </div>
  );
}
